import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import localeEsCL from '@angular/common/locales/es-CL';
import { LOCALE_ID } from '@angular/core';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { bootstrapApplication } from '@angular/platform-browser';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { authInterceptor } from '@interceptors/auth.interceptor';
import { provideIonicAngular } from '@ionic/angular/standalone';
import Clarity from '@microsoft/clarity';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';

function loadGoogleTagManager(containerId: string): void {
  if (!containerId) {
    return;
  }
  const gtmWindow = window as Window & { dataLayer?: unknown[]; };
  gtmWindow.dataLayer = gtmWindow.dataLayer ?? [];
  gtmWindow.dataLayer.push({ 'event': 'gtm.js', 'gtm.start': Date.now() });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

if (['prod', 'dev'].includes(environment.name)) {
  Clarity.init(environment.microsoftClarityProjectId);
  loadGoogleTagManager(environment.gtmContainerId);
}

registerLocaleData(localeEsCL);

bootstrapApplication(AppComponent, {
  providers: [
    provideIonicAngular({ animated: false, mode: 'ios' }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor]),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-XSRF-TOKEN',
      }),
    ),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.name === 'local') {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      }
      return auth;
    }),
    provideAnalytics(() => getAnalytics()),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.name === 'local') {
        connectFirestoreEmulator(firestore, 'localhost', 8080);
      }
      return firestore;
    }),
    ScreenTrackingService,
    UserTrackingService,
    provideStorage(() => {
      const storage = getStorage();
      if (environment.name === 'local') {
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      return storage;
    }),
    {
      provide: LOCALE_ID,
      useValue: 'es-CL',
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !environment.local,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
});
